import { Injector, runInInjectionContext } from '@angular/core';
import type { EntityBoardContext } from './context';

/**
 * What the board's create chord (`N`) should run right now.
 *
 * Context-sensitive by design: "create" on a board with a row focused means "create a child of THAT
 * row", and only the mount knows whether that is possible. With nothing focused the concept degrades
 * to the board's own Add, which is what the header's + button already does.
 *
 * Resolution, in order:
 * 1. focused item present → ask the mount via `itemInteraction.createChild`. Its answer is FINAL,
 *    including a null one: a focused row that cannot take a child does nothing, rather than quietly
 *    creating an unrelated top-level record the user never aimed at.
 * 2. no focused item → the header's `onAdd`, unless `hideAdd` says this mount has no Add at all
 *    or `addBlockedReason` has disabled +.
 *
 * A readonly mount reaches neither branch, because `resolveContext` strips `createChild` and forces
 * `hideAdd`. That stripping is the enforcement point — this helper needs no readonly check of its own.
 *
 * `createChild` runs in the board's injection context when an injector is given, the same way
 * `bulkCommands` does, so a mount can `inject(EntityBoardStore)` to reach the grid for a reveal.
 */
export function resolveBoardCreateAction<T = any>(args: { context: EntityBoardContext<T> | null | undefined; focusedItem: T | null | undefined; injector?: Injector }): (() => void) | null {

  const { context, focusedItem, injector } = args;
  if (!context) return null;

  if (focusedItem != null) {
    const createChild = context.itemInteraction?.createChild;
    if (!createChild) return null;
    return (injector ? runInInjectionContext(injector, () => createChild(focusedItem)) : createChild(focusedItem)) ?? null;
  }

  const header = context.header;
  if (!header || header.hideAdd || header.addBlockedReason) return null;

  return header.onAdd ?? null;
}

/** Resolves Shift+N only against a focused row; adding a checklist has no unrelated root fallback. */
export function resolveBoardChecklistCreateAction<T = any>(args: { context: EntityBoardContext<T> | null | undefined; focusedItem: T | null | undefined; injector?: Injector }): (() => void) | null {

  const { context, focusedItem, injector } = args;
  if (!context || focusedItem == null) return null;

  const createChildrenFromChecklist = context.itemInteraction?.createChildrenFromChecklist;
  if (!createChildrenFromChecklist) return null;

  return (injector ? runInInjectionContext(injector, () => createChildrenFromChecklist(focusedItem)) : createChildrenFromChecklist(focusedItem)) ?? null;
}
